import type { CSSProperties, FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";

import { useConfig } from "@jti/app-context/useConfig";
import { Card } from "@jti/core-components/components/Card";
import { Separator } from "@jti/core-components/components/Separator";
import { BREAKPOINT_SMALL_LARGE } from "@jti/fe-common/constants";
import type { Product as CartItem } from "@jti/magento";
import { ICartItemErrorType, usePdpLink } from "@jti/magento";
import { useSelector } from "@jti/store/reduxHooks";
import { Price, ProductMiniature, QuantityInput, useTranslations } from "@jti/ui";

import { ConsentItemFlag } from "@checkout/components/CartItem/ItemFlag";
import { RemoveItemDialog } from "@checkout/components/RemoveItemDialog";
import { usePromotionalOffers } from "@checkout/hooks/usePromotionalOffers";
import { hasCartErrorCode } from "@checkout/utils/getCartErrors";

import { CartLimitError } from "../CartLimitError";
import { SubscriptionItemFlag } from "../ItemFlag/SubscriptionItemFlag";
import { ProductDescription } from "../ProductDescription";
import { ProductPoints } from "../ProductPoints";
import { RegionBadge } from "../RegionBadge";
import { RemoveCartItemButton } from "../RemoveCartItemButton";
import { computeQuantityOrderLimit } from "../utils/computeQuantityOrderLimit";

import { TradeInSelect } from "./TradeInSelect";

import styles from "./RegularItem.module.scss";

export type RegularItemProps = {
  item: CartItem;
  qtyPending?: boolean;
  blockQuantityAdd?: boolean;
  isLimitEnabled?: boolean;
  isRemovable?: boolean;
  onRemoveClick(id: string | undefined): void;
  isQuantityInputVisible?: boolean;
  onQuantityChange(id: string | undefined, quantity: number): void;
};

export const RegularItem: FC<RegularItemProps> = ({
  qtyPending = false,
  item,
  blockQuantityAdd = false,
  isRemovable = true,
  onRemoveClick,
  isQuantityInputVisible = true,
  onQuantityChange,
}) => {
  const { __ } = useTranslations();
  const isScreenAboveMobile = useMediaQuery({ minWidth: BREAKPOINT_SMALL_LARGE });

  const [isOpenRemoveDialog, setIsOpenRemoveDialog] = useState(false);
  const {
    id,
    sku,
    imageUrl,
    name,
    quantity,
    price,
    description,
    status,
    points,
    enable_order_quantity_limit,
    order_quantity_limit,
    promotionalOffers,
    errors,
    tradeIn,
    has_registration_consent,
    nicotineStrength,
  } = item;
  const [limitMessageStatus, setLimitMessageStatus] = useState(false);
  const [isQuantityLimitReached, setIsQuantityLimitReached] = useState(false);
  const grandTotalPrice = useSelector((state) => state.cart.prices.data?.grandTotal.value) ?? 0;
  const shippingPrice = useSelector((state) => state.cart.prices.data?.shipping?.value) ?? 0;
  const maxOrderLimit = useSelector((state) => state.cart.storeConfig?.order_limit_value);
  const orderLimitEnabled = useSelector((state) => state.cart.storeConfig?.is_order_limit_enabled);
  const { SHOP_URL, PDP_URL } = useConfig();
  const pdpLink = usePdpLink(sku, { shopUrl: SHOP_URL, pdpUrl: PDP_URL ?? "" });
  const isOutOfStock = (errors && hasCartErrorCode(errors, ICartItemErrorType.ItemQty)) || status === "OUT_OF_STOCK";
  const isCartonsLimitReached = (errors && hasCartErrorCode(errors, ICartItemErrorType.CartonsLimit)) || false;
  const {
    isLimitReached: isSpecialOfferLimitReached,
    hasPromotionalOffers,
    promotionalOfferLimit,
  } = usePromotionalOffers(quantity, promotionalOffers);
  const isSubscriptionItem = item.subscription === 1;

  const mobileGridAreas = [
    '"product product"',
    '"separator separator"',
    isSubscriptionItem ? '"subscription subscription"' : null,
    tradeIn ? '"tradeInSelect tradeInSelect"' : null,
    '"price price"',
    '"quantity actions"',
    '"cardFooterDescription cardFooterDescription"',
  ]
    .filter(Boolean)
    .join(" ");

  const quantityOrderLimit = computeQuantityOrderLimit({
    hasPromotionalOffers,
    promotionalOfferLimit,
    tradeIn,
    enable_order_quantity_limit,
    order_quantity_limit,
  });

  useEffect(() => {
    const skuLimitReached = quantity >= order_quantity_limit && enable_order_quantity_limit === 1;
    const specialOfferLimitReached = hasPromotionalOffers && isSpecialOfferLimitReached;

    setIsQuantityLimitReached(skuLimitReached || specialOfferLimitReached);
  }, [quantity, hasPromotionalOffers, isSpecialOfferLimitReached, enable_order_quantity_limit, order_quantity_limit]);

  useEffect(() => {
    setLimitMessageStatus(grandTotalPrice + price.value / quantity - shippingPrice > maxOrderLimit);
  }, [grandTotalPrice]);

  const showRemoveItemDialog = useCallback(() => {
    setIsOpenRemoveDialog(true);
  }, [setIsOpenRemoveDialog]);

  const handleOnCancel = useCallback(() => {
    setIsOpenRemoveDialog(false);
  }, [setIsOpenRemoveDialog]);

  const handleOnRemove = useCallback(() => {
    onRemoveClick(id);
    handleOnCancel();
  }, [id, onRemoveClick, handleOnCancel]);

  const handleQuantityChange = useCallback(
    (value: number) => {
      onQuantityChange(id, value);
    },
    [id, onQuantityChange]
  );

  const isAddDisabled = blockQuantityAdd || isQuantityLimitReached;

  return (
    <Card variant="level-2">
      <div
        className={styles.rows}
        style={{ "--mobile-grid-areas": mobileGridAreas } as CSSProperties}
        data-testid="main-section"
      >
        <div className={styles.product}>
          <ProductMiniature
            url={pdpLink}
            name={name}
            imageUrl={imageUrl}
            outOfStock={isOutOfStock}
            variant="variantBig"
            badge={item.delivery_region_code ? <RegionBadge regionCode={item.delivery_region_code} /> : undefined}
            nicotineStrength={nicotineStrength}
          >
            {has_registration_consent && <ConsentItemFlag />}
            {isSubscriptionItem && <SubscriptionItemFlag className={styles.subscriptionProductFlag} />}
          </ProductMiniature>
        </div>
        {isSubscriptionItem && <SubscriptionItemFlag className={styles.subscriptionProductFlag} />}
        {isRemovable && (
          <div className={styles.actions}>
            <RemoveCartItemButton onClick={showRemoveItemDialog} />
          </div>
        )}
        <Price
          price={price}
          className={styles.price}
          frequency={isSubscriptionItem ? __("cart.cart_item.subscription_billing_frequency") : undefined}
        />

        {tradeIn && (
          <div className={styles.tradeInSelect}>
            <TradeInSelect sku={sku} />
          </div>
        )}

        <div className={styles.separator}>
          <Separator />
        </div>
        <div className={styles.cardFooterDescription}>
          <ProductPoints pointsQuantity={points || 0} />
          <ProductDescription description={description} visibleOn="all" />
        </div>

        {isQuantityInputVisible && (
          <div className={styles.quantity}>
            <div className={styles.quantityInputContainer}>
              <QuantityInput
                align={isScreenAboveMobile ? "end" : "start"}
                value={quantity}
                disabled={isOutOfStock || qtyPending}
                disabledAdd={isAddDisabled}
                max={quantityOrderLimit.isLimit === 1 ? quantityOrderLimit.quantityLimit : undefined}
                title={isOutOfStock ? __("cart.cart_item.out_of_stock") : undefined}
                onChange={handleQuantityChange}
                orderLimitEnabled={orderLimitEnabled}
                isOrderLimitReached={limitMessageStatus}
                maxOrderLimit={maxOrderLimit}
                currency={price.currency}
              />
            </div>
          </div>
        )}
      </div>

      <CartLimitError
        isCartonsLimitReached={isCartonsLimitReached}
        isQuantityLimitReached={isQuantityLimitReached}
        quantityLimit={quantityOrderLimit.quantityLimit}
      />

      <RemoveItemDialog
        item={item}
        isOpenDialog={isOpenRemoveDialog}
        onRemove={handleOnRemove}
        onCancel={handleOnCancel}
      />
    </Card>
  );
};
